import { useState, useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import LegalAssistant from './components/LegalAssistant';
import HomePage from './pages/HomePage';
import MarketplacePage from './pages/MarketplacePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CacServicesPage from './pages/CacServicesPage';
import LegalSupportPage from './pages/LegalSupportPage';
import RegisterPage from './pages/RegisterPage';
import AdminPanelPage from './pages/AdminPanelPage';
import RegistrationModal from './components/RegistrationModal';
import FaqPage from './pages/FaqPage';
import SupportPage from './pages/SupportPage';
import ProfilePage from './pages/ProfilePage';
import StorePage from './pages/StorePage';
import CheckoutPage from './pages/CheckoutPage';
import ClubPage from './pages/ClubPage';
import { ChatProvider } from './context/ChatContext';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import CartDrawer from './components/CartDrawer';
import LoginPage from './pages/LoginPage';
import { Page } from './types';
import { useUserActivity } from './hooks/useUserActivity';

interface ViewState {
    page: Page;
    productId?: number;
    storeId?: number;
}

// ─── Roteamento por URL (links compartilháveis, ex: /club) ───
const PAGE_TO_PATH: Record<Page, string> = {
    home: '/',
    marketplace: '/marketplace',
    product: '/produto',
    'cac-services': '/servicos-cac',
    'legal-support': '/juridico',
    faq: '/faq',
    support: '/suporte',
    profile: '/perfil',
    store: '/loja',
    register: '/cadastro',
    admin: '/admin',
    checkout: '/checkout',
    club: '/club',
    login: '/login',
};

function pathForView(view: ViewState): string {
    const base = PAGE_TO_PATH[view.page] || '/';
    if (view.page === 'product' && view.productId) return `${base}/${view.productId}`;
    if (view.page === 'store' && view.storeId) return `${base}/${view.storeId}`;
    return base;
}

function viewFromPath(pathname: string): ViewState {
    const clean = pathname.replace(/\/+$/, '') || '/';
    const [, seg, idStr] = clean.split('/');
    if (!seg) return { page: 'home' };
    const entry = (Object.entries(PAGE_TO_PATH) as [Page, string][]).find(
        ([, p]) => p === `/${seg}`,
    );
    if (!entry) return { page: 'home' };
    const page = entry[0];
    const id = idStr ? Number(idStr) : undefined;
    if (page === 'product' && id) return { page, productId: id };
    if (page === 'store' && id) return { page, storeId: id };
    return { page };
}

export default function App() {
    const [view, setView] = useState<ViewState>(() =>
        typeof window !== 'undefined' ? viewFromPath(window.location.pathname) : { page: 'home' },
    );
    const { activity, trackNavigation } = useUserActivity(view.page);
    const [isRegisterModalOpen, setRegisterModalOpen] = useState(false);

    // botão voltar/avançar do navegador
    useEffect(() => {
        const onPop = () => setView(viewFromPath(window.location.pathname));
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    const handleNavigate = (page: Page, id?: number) => {
        const newView: ViewState = { page };
        if (page === 'product') {
            newView.productId = id;
        } else if (page === 'store') {
            newView.storeId = id;
        }
        setView(newView);
        const path = pathForView(newView);
        if (window.location.pathname !== path) {
            window.history.pushState({}, '', path);
        }
        trackNavigation(page, id);
        window.scrollTo(0, 0);
    };

    const renderPage = () => {
        switch (view.page) {
            case 'checkout':
                return <CheckoutPage onNavigate={handleNavigate} />;
            case 'club':
                return <ClubPage onNavigate={handleNavigate} />;
            case 'marketplace':
                return <MarketplacePage onNavigate={handleNavigate} />;
            case 'product':
                if (view.productId) {
                    return <ProductDetailPage productId={view.productId} onNavigate={handleNavigate} />;
                }
                return <MarketplacePage onNavigate={handleNavigate} />;
            case 'store':
                if (view.storeId) {
                    return <StorePage storeId={view.storeId} onNavigate={handleNavigate} />;
                }
                return <MarketplacePage onNavigate={handleNavigate} />;
            case 'cac-services':
                return <CacServicesPage />;
            case 'legal-support':
                return <LegalSupportPage />;
            case 'faq':
                return <FaqPage />;
            case 'support':
                return <SupportPage />;
            case 'profile':
                return <ProfilePage />;
            case 'register':
                return <RegisterPage onNavigate={handleNavigate} />;
            case 'admin':
                return <AdminPanelPage onNavigate={handleNavigate} />;
            case 'login':
                return <LoginPage onNavigate={handleNavigate} />;
            case 'home':
            default:
                return <HomePage onNavigate={handleNavigate} />;
        }
    };

    return (
        <ChatProvider activity={activity}>
            <AuthProvider>
                <CartProvider>
                    <div className="bg-gray-900 text-white min-h-screen flex flex-col">
                        <Header onNavigate={handleNavigate} onRegisterClick={() => setRegisterModalOpen(true)} />
                        <CartDrawer onNavigate={handleNavigate} />
                        <main className="flex-grow">
                            {renderPage()}
                        </main>
                        <Footer onNavigate={handleNavigate} />
                        <LegalAssistant />
                        <RegistrationModal isOpen={isRegisterModalOpen} onClose={() => setRegisterModalOpen(false)} />
                    </div>
                </CartProvider>
            </AuthProvider>
        </ChatProvider>
    );
}
